import { RequestError } from '#errors/index.ts';
import { buildUrl, trimTrailingSlash } from '#utils/index.ts';
import {
  DEFAULT_TIMEOUT,
  throwConnectionError,
  throwResponseError,
  withSessionCookie,
  withTimeout,
} from '#connection/connection.ts';
import { buildRequest } from './http.helpers.ts';
import type {
  THttpClient,
  THttpClientOptions,
  THttpResponseType,
} from './http.types.ts';

const decodeJson = async <TResponse>(
  response: Response,
): Promise<TResponse> => {
  const text = await response.text();

  if (text.length === 0) {
    return undefined as TResponse;
  }

  try {
    return JSON.parse(text) as TResponse;
  } catch (cause) {
    throw new RequestError(
      { kind: 'decode', retryable: false, status: response.status },
      { cause },
    );
  }
};

const decode = async <TResponse>(
  response: Response,
  responseType: THttpResponseType,
): Promise<TResponse> => {
  switch (responseType) {
    case 'json':
      return decodeJson<TResponse>(response);
    case 'text':
      return (await response.text()) as TResponse;
    case 'blob':
      return (await response.blob()) as TResponse;
    case 'arrayBuffer':
      return (await response.arrayBuffer()) as TResponse;
    case 'bytes':
      return (await response.bytes()) as TResponse;
    case 'formData':
      return (await response.formData()) as TResponse;
    case 'stream':
      return response.body as TResponse;
    case 'response':
      return response as TResponse;
  }
};

const readResponse = <TResponse>(
  response: Response,
  responseType: THttpResponseType = 'json',
): Promise<TResponse> =>
  response.ok
    ? decode<TResponse>(response, responseType)
    : throwResponseError(response);

export const createHttpClient = ({
  url,
  onRequest = withSessionCookie,
  onResponse,
  fetch: send = globalThis.fetch,
  timeout = DEFAULT_TIMEOUT,
  headers = {},
  init = {},
}: THttpClientOptions): THttpClient => {
  const base = trimTrailingSlash(url);

  const request: THttpClient['request'] = async (
    method,
    path,
    body,
    options = {},
  ) => {
    const timer = withTimeout(options.signal, options.timeout ?? timeout);

    try {
      const request = buildRequest(
        { method, body, options, headers, init },
        timer.signal,
      );

      const requestInit = await onRequest(request);
      const url = buildUrl(base, path, options.query);
      const response = await send(url, requestInit);

      await onResponse?.(response);

      return await readResponse(response, options.responseType);
    } catch (cause) {
      return throwConnectionError(cause);
    } finally {
      timer.release();
    }
  };

  return {
    request,
    get: (path, options) => request('GET', path, undefined, options),
    post: (path, body, options) => request('POST', path, body, options),
    put: (path, body, options) => request('PUT', path, body, options),
    patch: (path, body, options) => request('PATCH', path, body, options),
    delete: (path, options) => request('DELETE', path, undefined, options),
  };
};
